import React, { useEffect } from 'react'

import { kycInReopen } from 'constants/feature-keys'

import { Spinner } from '@acorns/web-components'
import { useFeature } from '@acorns/web-toggles-react'
import { useEnv } from '@acorns/web-utils'

import { DynamicPricingInterstitial } from 'components/DynamicPricingInterstitial'
import {
  User,
  useInvestAccountQuery,
  useReopenUserAccountMutation,
  useSubscribeToTierMutation,
} from 'generated/types'
import { useCaptureDeviceSessionId } from 'hooks/captureDeviceSessionId'
import { usePersistedState } from 'hooks/usePersistedState'

import { loadSocureDeviceRiskSdk } from '../../vendors/socure'
import { GetStep } from './ClosedAccount'
import { REOPEN_FUNNEL } from './events'
import { CenteredSpinner } from './styled-components'

interface Props {
  setStep: (index: number) => void
  getStep: GetStep
  user: User
  isLongReopen?: boolean
}

export const REOPEN_FLOW_SUBSCRIPTION_KEY = 'ACCOUNT_REOPEN_SUBSCRIPTION'

export const Pricing = (props: Props) => {
  const { env } = useEnv()
  const { data: investmentAccountData, loading } = useInvestAccountQuery()
  const [isKycInReopenEnabled] = useFeature(kycInReopen)
  const [subscribeToTier, { loading: isSubscribing }] =
    useSubscribeToTierMutation()
  const [reopenCustomerAccount, { loading: isReopeningAccount }] =
    useReopenUserAccountMutation()
  const captureDeviceSessionId = useCaptureDeviceSessionId()
  const [, setSubscriptionState] = usePersistedState(
    REOPEN_FLOW_SUBSCRIPTION_KEY,
    JSON.stringify({}),
    'localStorage',
  )

  useEffect(() => {
    if (!isKycInReopenEnabled || window.SigmaDeviceManager) {
      return
    }

    loadSocureDeviceRiskSdk(env.get('socureSdkKey'))
  }, [isKycInReopenEnabled, env])

  const handleCtaPress = async (tierId: string, tierPriceId: string) => {
    const subscriptionInput = {
      tierPriceId,
      tierId,
    }

    if (isKycInReopenEnabled) {
      captureDeviceSessionId()

      await subscribeToTier({
        variables: {
          input: {
            ...subscriptionInput,
            userId: props.user.uuid,
          },
        },
      })

      await reopenCustomerAccount()
      setSubscriptionState(JSON.stringify({}))
    } else {
      setSubscriptionState(JSON.stringify(subscriptionInput))
    }
    props.setStep(props.getStep('Portfolio'))
  }

  if (loading) {
    return (
      <CenteredSpinner>
        <Spinner />
      </CenteredSpinner>
    )
  }

  return (
    <DynamicPricingInterstitial
      handleCtaPress={handleCtaPress}
      eventFunnelValue={REOPEN_FUNNEL}
      isLongReopen={props.isLongReopen ?? false}
      portfolioRisk={
        investmentAccountData?.investmentAccountsByUserId?.[0]?.portfolio
          ?.riskLevel
      }
      loading={isSubscribing || isReopeningAccount}
    />
  )
}
